import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { useSurfaceSync } from '@hooks/useSurface';
import {
  LayoutDashboard, BrainCircuit, Layers, Wallet, Settings, LogOut,
  Menu, X, Mic, PenTool, VenetianMask, ChevronRight,
} from 'lucide-react';

/* Single motion contract shared by the rail's width, label opacity/width,
   and the toggle chevron's rotation, so they read as one thing moving
   instead of several independently-timed parts. prefers-reduced-motion
   strips all of them the same way (see useReducedMotion below). */
const RAIL_MOTION = '220ms cubic-bezier(0.4, 0, 0.2, 1)';

const MessageSquareText = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"/>
    <path d="M7 11h10"/><path d="M7 15h6"/><path d="M7 7h8"/>
  </svg>
);

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

/* ─── Admin flag ─── */
const ADMIN_EMAIL = 'plethora1000@gmail.com';
const isAdmin = (email) => email?.toLowerCase() === ADMIN_EMAIL;

const NAV_ITEMS = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/skills',    label: 'Skills',    icon: Layers },
  { to: '/feedback',  label: 'Feedback',  icon: MessageSquareText },
  { to: '/billing',   label: 'Billing',   icon: Wallet },
];

// Public skills, in display order. AI Prompting is the only fully public skill
// right now. Sales & Negotiation, Copywriting, and Precision Storytelling are
// unfinished and admin-only — appended in skillNavItems below so the admin order
// is AI Prompting → Sales → Copywriting → Precision Storytelling.
const SKILL_NAV = [
  { to: '/skills/ai-prompting',         label: 'AI Prompting',         icon: BrainCircuit, accent: '#ea580c' },
];

const STORAGE_KEY = 'ca_sidebar_collapsed';

/* ─── Route tint — sidebar chameleon ─── */
const ROUTE_TINTS = {
  '/skills/ai-prompting':      'rgba(234, 88,  12,  0.07)',
  '/skills/sales-negotiation': 'rgba(3,   105, 161, 0.08)',
  '/skills/copywriting':       'rgba(196, 184, 122, 0.07)', /* warm paper ink — matches copywriting bg */
  '/skills/precision-storytelling': 'rgba(219, 39, 119, 0.07)', /* rose — matches storytelling accent */
  '/voice-room':               'rgba(3,   105, 161, 0.07)', /* Sales & Negotiation's practice room — same accent as the skill it belongs to */
  '/dashboard':                'rgba(234, 88,  12,  0.04)',
};
const getRouteTint = (pathname) => {
  for (const [route, color] of Object.entries(ROUTE_TINTS)) {
    if (pathname.startsWith(route)) return color;
  }
  return 'rgba(234, 88, 12, 0.02)';
};

/* ─── Color tokens for adaptive sheet ───
   MOBILE ONLY from here on — the mobile drawer + hamburger button, plus
   every internal divider/text color in SidebarContent when mobile=true,
   still key off the global isDark flag via this object, untouched by
   the glass-rail refactor.
   The DESKTOP rail must not read isDark anywhere (it has to match the
   PAGE underneath it, which can be pinned to one theme independent of
   the user's global toggle — see hooks/useSurface.js) — its equivalent
   colors come from RAIL_TOKENS below instead, which are plain CSS
   var() strings resolved by the browser against whatever [data-surface]
   is currently set, not by this component picking light vs dark itself. */
const SHEET = {
  light: {
    base:    'rgba(250, 250, 249, 0.55)',   // was 0.92
    hover:   'rgba(250, 250, 249, 0.72)',   // was 0.55
    border:  'rgba(0, 0, 0, 0.06)',
    text:    '#57534e',
    textDim: '#a8a29e',
    activeBg: 'rgba(234, 88, 12, 0.08)',
  },
  dark: {
    base:    'rgba(12, 10, 9, 0.55)',       // was 0.88
    hover:   'rgba(12, 10, 9, 0.72)',       // was 0.45
    border:  'rgba(255, 255, 255, 0.06)',
    text:    '#a8a29e',
    textDim: '#78716c',
    activeBg: 'rgba(234, 88, 12, 0.15)',
  },
};

/* Desktop rail equivalent of SHEET — CSS variables, not a light/dark pair
   picked in JS, so the browser resolves them against [data-surface]
   directly and this component never has to know which one is active.
   `text` reads --rail-icon-rest: every row that uses it pairs an icon
   with a label sharing one `color` (icons inherit currentColor from
   their <Link>/<button>, same as the label span does), so "icon rest
   colour" and "this row's rest text colour" are the same value in this
   component — there's no row here with icon-only or text-only color
   needs that would call for two separate tokens. */
const RAIL_TOKENS = {
  border:    'var(--rail-glass-border)',
  text:      'var(--rail-icon-rest)',
  textDim:   'var(--rail-text-dim)',
  iconHover: 'var(--rail-icon-hover)',
  active:    'var(--rail-icon-active)',
  activeBg:  'var(--rail-active-bg)',
};

/* ─── Reduced motion ─── same matchMedia-effect shape as the isDark
   MutationObserver below: external state, not a prop, so any component
   using this hook stays correct if the OS setting changes mid-session. */
const useReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
};

/* ─── Rail divider ───
   One reusable element for all 4 boundaries (below the logo, below the
   main nav items, below the X link, below Settings/Sign out) so they
   share the exact same inset, thickness, and colour instead of each
   being a different ambient border on a differently-padded container.
   The inset (8px each side) matches nav's own `px-2`, i.e. the one
   divider that already read as inset before this normalisation — the
   others used to be full-bleed borders on unpadded wrapper divs.
   Colour comes from `t.border` (--rail-glass-border on desktop, the
   [data-surface] token), never a hardcoded value or the global .dark
   class — same source as before, just applied uniformly now. */
const RailDivider = ({ color }) => (
  <div style={{ height: 1, margin: '0 8px', background: color, flexShrink: 0 }} />
);

// ─── SidebarContent ────────────────────────────────────────────────────────────
const SidebarContent = ({
  collapsed,
  setCollapsed,
  mobile = false,
  closeMobile,
  isAdminUser,
  isHovered,
  isDark,
  reduced = false,
}) => {
  const location = useLocation();
  const { signOut } = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = useCallback(async () => {
    setSigningOut(true);
    try {
      await signOut();
      window.location.href = '/';
    } catch {
      setSigningOut(false);
    }
  }, [signOut]);

  const showLabels = mobile || !collapsed;
  /* Mobile keeps its exact pre-refactor isDark-driven colors. Desktop
     reads RAIL_TOKENS (CSS var() strings resolved against [data-surface])
     instead — the rail must not decide light vs dark itself. Same split
     for the hover/active colors below, which weren't plain object
     lookups before (they were isDark ternaries inline in each
     onMouseEnter/Leave handler) and needed the same treatment. */
  const t = mobile ? (isDark ? SHEET.dark : SHEET.light) : RAIL_TOKENS;
  const hoverColor = mobile ? (isDark ? '#fafaf9' : '#1c1917') : RAIL_TOKENS.iconHover;
  const activeColor = mobile ? (isDark ? '#fb923c' : '#ea580c') : RAIL_TOKENS.active;
  // Neutral (theme-agnostic) wash for desktop hover backgrounds — alpha
  // blending toward mid-grey reads as a hover state on either surface
  // without the rail needing to know which one is under it.
  const hoverBg = mobile ? (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)') : 'rgba(127,127,127,0.10)';

  const skillNavItems = [...SKILL_NAV];
  // Admin-only skills (unfinished): Sales & Negotiation, Copywriting, and
  // Precision Storytelling. Hidden from non-admins' nav entirely.
  if (isAdminUser) {
    skillNavItems.push(
      { to: '/skills/sales-negotiation',      label: 'Sales & Negotiation',    icon: Mic,           accent: '#0ea5e9' },
      { to: '/skills/copywriting',            label: 'Copywriting',            icon: PenTool,       accent: '#7c3aed' },
      { to: '/skills/precision-storytelling', label: 'Precision Storytelling', icon: VenetianMask,  accent: '#db2777' },
    );
  }

  return (
    <div
      className="flex flex-col h-full"
      style={{
        width: mobile ? 288 : collapsed ? 60 : 240,
        transition: reduced ? 'none' : `width ${RAIL_MOTION}`,
      }}
    >
      {/* ── Logo ── */}
      <div
        className="flex items-center h-16 shrink-0"
        style={{
          padding: '0 16px',
          justifyContent: 'flex-start',
          gap: 12,
        }}
      >
        <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity min-w-0">
          <img
            src="/logo.svg" alt="Castle Atlas"
            className={mobile ? 'w-7 h-7 shrink-0 dark:brightness-0 dark:invert' : 'w-7 h-7 shrink-0'}
            style={mobile ? undefined : { filter: 'var(--rail-logo-filter)' }}
          />
          <span
            className="text-sm font-bold tracking-tight truncate"
            style={{
              color: hoverColor,
              opacity: showLabels ? 1 : 0,
              transition: reduced ? 'none' : `opacity ${RAIL_MOTION}`,
              pointerEvents: showLabels ? 'auto' : 'none',
            }}
          >
            Castle Atlas
          </span>
        </Link>
        {mobile && closeMobile && (
          <button
            onClick={closeMobile}
            className="ml-auto p-1.5 rounded-lg transition-colors"
            style={{ color: t.textDim }}
            onMouseEnter={e => e.currentTarget.style.color = isDark ? '#d4cdc7' : '#57534e'}
            onMouseLeave={e => e.currentTarget.style.color = t.textDim}
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        )}
      </div>

      <RailDivider color={t.border} />

      {/* ── Nav ── */}
      <nav className="py-4 overflow-y-auto overflow-x-hidden">

        {/* Main nav items */}
        <div className="px-2 space-y-0.5">
        {NAV_ITEMS.map(({ to, label, icon: Icon }) => {
          const active = to === '/skills'
            ? location.pathname === '/skills'
            : location.pathname.startsWith(to);
          return (
            <Link
              key={to}
              to={to}
              title={collapsed && !mobile ? label : undefined}
              className="flex items-center rounded-xl transition-all duration-150"
              style={{
                height: 40,
                width: '100%',
                margin: 0,
                // Collapsed: centered, zero gap/padding, so the icon alone
                // (the only child left with real width once the label
                // and dot both go to width:0 below) sits centered in the
                // 60px button instead of shoved left by invisible-but-
                // still-laid-out siblings.
                justifyContent: showLabels ? 'flex-start' : 'center',
                gap: showLabels ? 12 : 0,
                padding: showLabels ? '0 12px' : 0,
                background: active ? t.activeBg : 'transparent',
                color: active ? activeColor : t.text,
              }}
              onMouseEnter={e => {
                if (!active) {
                  e.currentTarget.style.background = hoverBg;
                  e.currentTarget.style.color = hoverColor;
                }
              }}
              onMouseLeave={e => {
                if (!active) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = t.text;
                }
              }}
            >
              <span className="shrink-0 w-[18px] h-[18px] flex items-center justify-center">
                <Icon size={18} />
              </span>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  opacity: showLabels ? 1 : 0,
                  transition: reduced ? 'none' : `opacity ${RAIL_MOTION}`,
                  pointerEvents: showLabels ? 'auto' : 'none',
                  flex: showLabels ? 1 : '0 0 0px',
                  width: showLabels ? 'auto' : 0,
                }}
              >
                {label}
              </span>
              <div
                className="rounded-full shrink-0"
                style={{
                  width: showLabels ? 6 : 0, height: 6,
                  background: active ? activeColor : 'transparent',
                  opacity: showLabels ? 1 : 0,
                  transition: reduced ? 'none' : `opacity ${RAIL_MOTION}`,
                }}
              />
            </Link>
          );
        })}
        </div>

        <div style={{ margin: '8px 0' }}>
          <RailDivider color={t.border} />
        </div>

        {/* ── Skill links ── */}
        <div
          className="px-2"
          style={{
            minHeight: showLabels ? 'auto' : 40,
          }}
        >
          {/* "Skills" label */}
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              padding: '0 12px',
              marginBottom: 4,
              color: t.textDim,
              opacity: showLabels ? 1 : 0,
              transition: reduced ? 'none' : `opacity ${RAIL_MOTION}`,
              pointerEvents: 'none',
              height: 20,
              lineHeight: '20px',
            }}
          >
            Skills
          </div>

          {skillNavItems.map(({ to, label, icon: Icon, accent }) => {
            const isActive =
              location.pathname.startsWith(to) ||
              (to === '/skills/sales-negotiation' && location.pathname.startsWith('/voice-room')) ||
              (to === '/skills/copywriting' && location.pathname.startsWith('/skills/copywriting'));
            return (
              <Link
                key={to}
                to={to}
                title={collapsed && !mobile ? label : undefined}
                className="flex items-center rounded-xl transition-all duration-150"
                style={{
                  height: 36,
                  width: '100%',
                  margin: 0,
                  justifyContent: showLabels ? 'flex-start' : 'center',
                  gap: showLabels ? 12 : 0,
                  padding: showLabels ? '0 12px' : 0,
                  background: isActive ? `${accent}15` : 'transparent',
                  color: isActive ? accent : t.text,
                }}
                onMouseEnter={e => {
                  if (!isActive) {
                    e.currentTarget.style.background = hoverBg;
                    e.currentTarget.style.color = hoverColor;
                  }
                }}
                onMouseLeave={e => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = t.text;
                  }
                }}
              >
                <span className="shrink-0 w-[18px] h-[18px] flex items-center justify-center">
                  <Icon size={16} />
                </span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    opacity: showLabels ? 1 : 0,
                    transition: reduced ? 'none' : `opacity ${RAIL_MOTION}`,
                    pointerEvents: showLabels ? 'auto' : 'none',
                    flex: showLabels ? 1 : '0 0 0px',
                    width: showLabels ? 'auto' : 0,
                  }}
                >
                  {label}
                </span>
                <div
                  className="rounded-full shrink-0"
                  style={{
                    width: showLabels ? 6 : 0, height: 6,
                    background: isActive ? accent : 'transparent',
                    opacity: showLabels ? 1 : 0,
                    transition: reduced ? 'none' : `opacity ${RAIL_MOTION}`,
                  }}
                />
              </Link>
            );
          })}
        </div>
      </nav>

      {/* ── Flex spacer — pushes X/Settings/Sign out (and the toggle,
          below its own divider) down to the bottom regardless of how
          few nav items there are, instead of the nav block itself
          stretching (which is what flex-1 on <nav> used to do). ── */}
      <div style={{ flex: 1 }} />

      {/* ── Follow on X ── */}
      <div className="shrink-0 px-2 pb-2">
        <a
          href="https://twitter.com/castleatlas"
          target="_blank"
          rel="noopener noreferrer"
          title={collapsed && !mobile ? 'Follow us on X' : undefined}
          className="flex items-center rounded-xl transition-colors"
          style={{
            height: 40,
            gap: showLabels ? 12 : 0,
            padding: showLabels ? '0 12px' : 0,
            justifyContent: showLabels ? 'flex-start' : 'center',
            width: '100%',
            margin: 0,
            color: t.text,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = hoverBg;
            e.currentTarget.style.color = hoverColor;
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = t.text;
          }}
        >
          <span className="shrink-0 w-[18px] h-[18px] flex items-center justify-center">
            <XIcon />
          </span>
          <span
            style={{
              fontSize: 14,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              opacity: showLabels ? 1 : 0,
              transition: reduced ? 'none' : `opacity ${RAIL_MOTION}`,
              pointerEvents: showLabels ? 'auto' : 'none',
              flex: showLabels ? 1 : '0 0 0px',
              width: showLabels ? 'auto' : 0,
            }}
          >
            Follow us on X
          </span>
        </a>
      </div>

      <RailDivider color={t.border} />

      {/* ── Settings + Sign out ── */}
      <div
        className="shrink-0 py-3 px-2 space-y-0.5"
      >
        <Link
          to="/settings"
          title={collapsed && !mobile ? 'Settings' : undefined}
          className="flex items-center rounded-xl transition-all duration-150"
          style={{
            height: 40,
            gap: showLabels ? 12 : 0,
            padding: showLabels ? '0 12px' : 0,
            justifyContent: showLabels ? 'flex-start' : 'center',
            width: '100%',
            margin: 0,
            background: location.pathname.startsWith('/settings') ? t.activeBg : 'transparent',
            color: location.pathname.startsWith('/settings') ? activeColor : t.text,
          }}
          onMouseEnter={e => {
            if (!location.pathname.startsWith('/settings')) {
              e.currentTarget.style.background = hoverBg;
              e.currentTarget.style.color = hoverColor;
            }
          }}
          onMouseLeave={e => {
            if (!location.pathname.startsWith('/settings')) {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = t.text;
            }
          }}
        >
          <span className="shrink-0 w-[18px] h-[18px] flex items-center justify-center">
            <Settings size={18} />
          </span>
          <span
            style={{
              fontSize: 14,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              opacity: showLabels ? 1 : 0,
              transition: reduced ? 'none' : `opacity ${RAIL_MOTION}`,
              pointerEvents: showLabels ? 'auto' : 'none',
              flex: showLabels ? 1 : '0 0 0px',
              width: showLabels ? 'auto' : 0,
            }}
          >
            Settings
          </span>
        </Link>

        {/* ── Sign out ──
            FIX: Wrapped icon in consistent span wrapper.
            Removed w-full from className to match Settings.
            Added explicit alignItems and textAlign.
        ── */}
        <button
          onClick={handleSignOut}
          disabled={signingOut}
          title={collapsed && !mobile ? 'Sign out' : undefined}
          className="flex items-center rounded-xl transition-all duration-150"
          style={{
            height: 40,
            gap: showLabels ? 12 : 0,
            padding: showLabels ? '0 12px' : 0,
            justifyContent: showLabels ? 'flex-start' : 'center',
            alignItems: 'center',
            width: '100%',
            margin: 0,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: t.text,
            textAlign: 'left',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = mobile
              ? (isDark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.06)')
              : 'rgba(239,68,68,0.07)';
            e.currentTarget.style.color = '#ef4444';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = t.text;
          }}
        >
          <span className="shrink-0 w-[18px] h-[18px] flex items-center justify-center">
            {signingOut
              ? <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
              : <LogOut size={18} />
            }
          </span>
          <span
            style={{
              fontSize: 14,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              opacity: showLabels ? 1 : 0,
              transition: reduced ? 'none' : `opacity ${RAIL_MOTION}`,
              pointerEvents: showLabels ? 'auto' : 'none',
              flex: showLabels ? 1 : '0 0 0px',
              width: showLabels ? 'auto' : 0,
              textAlign: 'left',
            }}
          >
            Sign out
          </span>
        </button>
      </div>

      {/* ── Collapse/expand toggle — divider, then the toggle as the very
          last item in the rail, per the requested bottom-to-top order.
          justify-content:center regardless of showLabels (unlike every
          row above) since this row has no label to make room for either
          way — centering it directly under the rest of the icon column
          reads better than pinning it to the same left edge as a
          labeled row would. Desktop only — mobile's drawer keeps its
          own X close button in the logo row for open/close. */}
      {!mobile && (
        <>
          <RailDivider color={t.border} />
          <div className="shrink-0 flex items-center justify-center" style={{ height: 44 }}>
          <button
            onClick={() => setCollapsed(c => !c)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            className="flex items-center justify-center rounded-lg"
            style={{
              width: 22, height: 22,
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              color: t.textDim,
              // Collapsed points right; expanded rotates the same element
              // 180deg to point left — one element rotating, not two icons
              // swapping. Same duration/easing as the rest of the rail's
              // motion (width, labels) so it all reads as one thing moving.
              transform: collapsed ? 'none' : 'rotate(180deg)',
              transition: reduced ? 'none' : `transform ${RAIL_MOTION}, color 150ms ease`,
            }}
            onMouseEnter={e => e.currentTarget.style.color = hoverColor}
            onMouseLeave={e => e.currentTarget.style.color = t.textDim}
          >
            <ChevronRight size={18} />
          </button>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Sidebar (shell) ─────────────────
const Sidebar = () => {
  const location = useLocation();
  const { user } = useAuth();
  const isAdminUser = isAdmin(user?.email);

  const [isDark, setIsDark] = useState(() =>
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  const [isHovered, setIsHovered] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      // Collapsed is the default state: only an explicit stored "false"
      // (the user has expanded it before) starts it expanded.
      return stored === null ? true : stored === 'true';
    } catch { return true; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const reduced = useReducedMotion();
  const asideRef = useRef(null);
  // Keeps <html data-surface> mirroring the global .dark class by default;
  // a skill page's own useForceSurface() call (see hooks/useSurface.js)
  // overrides this while that page is mounted. Runs unconditionally
  // (before the isExercisePage early return below) since it's a hook.
  useSurfaceSync();

  useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark'))
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, String(collapsed)); } catch {}
  }, [collapsed]);
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
        setCollapsed(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  /* Click outside the expanded rail collapses it. Only wired up while
     expanded — collapsed has nothing "outside" to click away from, and
     leaving the listener attached unconditionally would mean every
     click anywhere in the app pays for a contains() check it never
     needs. mousedown, not click, so this resolves before whatever the
     click target itself does (the standard outside-click ordering). */
  useEffect(() => {
    if (collapsed) return undefined;
    const handler = (e) => {
      if (asideRef.current && !asideRef.current.contains(e.target)) {
        setCollapsed(true);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [collapsed]);

  const isExercisePage = location.pathname.includes('/exercise');
  if (isExercisePage) return null;

  const sidebarWidth = collapsed ? 60 : 240;
  const t = isDark ? SHEET.dark : SHEET.light;
  const routeTint = getRouteTint(location.pathname);

  const desktopSidebarStyle = {
    position: 'fixed',
    /* Floating rail: inset off all three edges instead of docked at
       left:0/top:0/height:100vh — bottom:12 (not an explicit height)
       so the fixed box's own height is solved from top+bottom, same
       "let insets do the sizing" principle as elsewhere on this app. */
    top: 12, left: 12, bottom: 12,
    width: sidebarWidth,
    zIndex: 40, // checked against CelebrationModal (100), Settings toasts/modal (9999), Landing's own nav dropdown (200) — all comfortably above; nothing else in authenticated pages exceeds ~20
    overflow: 'hidden',
    borderRadius: 20,
    backdropFilter: 'blur(28px) saturate(180%)',
    WebkitBackdropFilter: 'blur(28px) saturate(180%)',
    // No border — the inset top/left highlight baked into --rail-glass-shadow
    // (an `inset 1px 1px 0` hairline) plus the outer drop-shadow after it are
    // now the rail's only edge definition. --rail-glass-border still exists
    // as a token (checked: still used by the 4 internal RailDivider elements
    // above), it's just no longer drawn as the aside's own outer border.
    border: 'none',
    boxShadow: 'var(--rail-glass-shadow)',
    // Route tint stays a low-opacity wash layered OVER the glass base
    // (unchanged technique) so the per-skill chameleon colour survives
    // the surface now being translucent rather than a solid sheet.
    background: `linear-gradient(180deg, ${routeTint} 0%, transparent 65%), ${isHovered ? 'var(--rail-glass-bg-hover)' : 'var(--rail-glass-bg)'}`,
    transition: reduced
      ? 'background 0.5s cubic-bezier(0.22, 1, 0.36, 1)'
      : `width ${RAIL_MOTION}, background 0.5s cubic-bezier(0.22, 1, 0.36, 1)`,
  };

  const mobileDrawerStyle = {
    transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
    transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
    overflow: 'hidden',
    background: t.base,
    borderRight: `1px solid ${t.border}`,
  };

  return (
    <>
      <aside
        ref={asideRef}
        className="hidden md:flex flex-col"
        style={desktopSidebarStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <SidebarContent
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          isAdminUser={isAdminUser}
          isHovered={isHovered}
          isDark={isDark}
          reduced={reduced}
        />
      </aside>

      {/* No reserved-space spacer here — AppLayout's <main> carries no
          padding at all now (it's the ancestor of every page's own
          background element, so padding here would push that background
          over with it — see AppLayout.jsx's own note). Each page's inner
          content wrapper instead gets a `rail-clearance` class (margin,
          defined in index.css: 0 below 768px, 72px — 60px collapsed
          rail + 12px inset — at 768px and up), so the background spans
          full width underneath the rail while only content is inset. */}

      {!mobileOpen && (
        <button
          onClick={() => setMobileOpen(true)}
          className="md:hidden fixed top-3 right-3 z-50 w-9 h-9 flex items-center justify-center rounded-xl shadow-sm"
          style={{
            background: isDark ? 'rgba(12,10,9,0.9)' : 'rgba(250,250,249,0.9)',
            border: `1px solid ${t.border}`,
            color: t.text,
            backdropFilter: 'blur(12px)',
          }}
          aria-label="Open menu"
        >
          <Menu size={18} />
        </button>
      )}

      <div
        className="md:hidden fixed inset-0 z-40"
        onClick={() => setMobileOpen(false)}
        style={{
          pointerEvents: mobileOpen ? 'auto' : 'none',
          background: mobileOpen ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0)',
          backdropFilter: mobileOpen ? 'blur(6px)' : 'blur(0px)',
          WebkitBackdropFilter: mobileOpen ? 'blur(6px)' : 'blur(0px)',
          transition: 'background 0.4s ease, backdrop-filter 0.4s ease',
        }}
      />

      <aside
        className="md:hidden fixed left-0 top-0 bottom-0 z-50 flex flex-col shadow-2xl"
        style={mobileDrawerStyle}
      >
        <SidebarContent
          collapsed={false}
          setCollapsed={setCollapsed}
          mobile
          closeMobile={() => setMobileOpen(false)}
          isAdminUser={isAdminUser}
          isHovered={false}
          isDark={isDark}
        />
      </aside>
    </>
  );
};

export default Sidebar;